import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { Text, Icon, RadioButton, DropdownItem, Button, isMobileStyleMixin, HelpTip, useIsMobile } from '@amzn/storm-ui';
import IntlMessageFormat from 'intl-messageformat';
import styled, { css } from 'styled-components';
import FilterDropdown, { FilterRadioGroup } from '@amzn/storm-ui-filter-dropdown';

const DEFAULT_LOCALE = 'en-US';

/**
 * For advertising family, list of supported locales is refereced from
 * https://w.amazon.com/bin/view/INTech/Panther/I18nFamily#Hadvertising
 */

const localeToTranslations = {
  'ar-AE': () => import( /*webpackChunkName: "currencySelector-ar-AE"*/'./currencySelector-ar-AE.puff-3c58040e.js'),
  'cs-CZ': () => import( /*webpackChunkName: "currencySelector-cs-CZ"*/'./currencySelector-cs-CZ.puff-bb1ff6cb.js'),
  'de-DE': () => import( /*webpackChunkName: "currencySelector-de-DE"*/'./currencySelector-de-DE.puff-09e59952.js'),
  'en-AU': () => import( /*webpackChunkName: "currencySelector-en-AU"*/'./currencySelector-en-AU.puff-79f281f8.js'),
  'en-CA': () => import( /*webpackChunkName: "currencySelector-en-CA"*/'./currencySelector-en-CA.puff-3ffc22b3.js'),
  'en-GB': () => import( /*webpackChunkName: "currencySelector-en-GB"*/'./currencySelector-en-GB.puff-00fa6486.js'),
  'en-IN': () => import( /*webpackChunkName: "currencySelector-en-IN"*/'./currencySelector-en-IN.puff-835d9041.js'),
  'en-SG': () => import( /*webpackChunkName: "currencySelector-en-SG"*/'./currencySelector-en-SG.puff-a646d707.js'),
  'en-US': () => import( /*webpackChunkName: "currencySelector-en-US"*/'./currencySelector-en-US.puff-57a2b957.js'),
  'es-CO': () => import( /*webpackChunkName: "currencySelector-es-CO"*/'./currencySelector-es-CO.puff-bcd4fe6f.js'),
  'es-ES': () => import( /*webpackChunkName: "currencySelector-es-ES"*/'./currencySelector-es-ES.puff-ef4180c8.js'),
  'es-MX': () => import( /*webpackChunkName: "currencySelector-es-MX"*/'./currencySelector-es-MX.puff-c68b58b3.js'),
  'fr-CA': () => import( /*webpackChunkName: "currencySelector-fr-CA"*/'./currencySelector-fr-CA.puff-20bf7337.js'),
  'fr-FR': () => import( /*webpackChunkName: "currencySelector-fr-FR"*/'./currencySelector-fr-FR.puff-9f33ef8b.js'),
  'he-IL': () => import( /*webpackChunkName: "currencySelector-he-IL"*/'./currencySelector-he-IL.puff-ef431a2d.js'),
  'hi-IN': () => import( /*webpackChunkName: "currencySelector-hi-IN"*/'./currencySelector-hi-IN.puff-a38f3350.js'),
  'it-IT': () => import( /*webpackChunkName: "currencySelector-it-IT"*/'./currencySelector-it-IT.puff-394e3579.js'),
  'ja-JP': () => import( /*webpackChunkName: "currencySelector-ja-JP"*/'./currencySelector-ja-JP.puff-6c6b5236.js'),
  'ko-KR': () => import( /*webpackChunkName: "currencySelector-ko-KR"*/'./currencySelector-ko-KR.puff-0cfe033b.js'),
  'nl-NL': () => import( /*webpackChunkName: "currencySelector-nl-NL"*/'./currencySelector-nl-NL.puff-08e47ea0.js'),
  'pl-PL': () => import( /*webpackChunkName: "currencySelector-pl-PL"*/'./currencySelector-pl-PL.puff-1767e07b.js'),
  'pt-BR': () => import( /*webpackChunkName: "currencySelector-pt-BR"*/'./currencySelector-pt-BR.puff-ffa86462.js'),
  'sv-SE': () => import( /*webpackChunkName: "currencySelector-sv-SE"*/'./currencySelector-sv-SE.puff-0efdeb1a.js'),
  'ta-IN': () => import( /*webpackChunkName: "currencySelector-ta-IN"*/'./currencySelector-ta-IN.puff-6c0e0b4c.js'),
  'th-TH': () => import( /*webpackChunkName: "currencySelector-th-TH"*/'./currencySelector-th-TH.puff-a0eee837.js'),
  'tr-TR': () => import( /*webpackChunkName: "currencySelector-tr-TR"*/'./currencySelector-tr-TR.puff-44694281.js'),
  'vi-VN': () => import( /*webpackChunkName: "currencySelector-vi-VN"*/'./currencySelector-vi-VN.puff-0730a0ba.js'),
  'zh-CN': () => import( /*webpackChunkName: "currencySelector-zh-CN"*/'./currencySelector-zh-CN.puff-6e4e78da.js'),
  'zh-TW': () => import( /*webpackChunkName: "currencySelector-zh-TW"*/'./currencySelector-zh-TW.puff-664bf913.js')
};
const localeToTranslations$1 = localeToTranslations;

/* eslint-disable @typescript-eslint/no-explicit-any */
// Original - https://code.amazon.com/packages/CAPWebsiteFrontend/blobs/mainline/--/packages/website/src/intl/puffjToICU.js

// Object.entries polyfills
const getObjectEntries = item => Object.keys(item).reduce((array, key) => {
  array.push([key, item[key]]);
  return array;
}, []);

// ICU plural message format: https://formatjs.io/guides/message-syntax/#plural-format
const toICUPluralMessage = message => `{${message.value.param}, plural,
${getObjectEntries(message.value.pluralItems).map(_ref => {
  let [pluralKey, pluralMessage] = _ref;
  return `${pluralKey} {${pluralMessage}}`;
}).join('\n')}}`;

// ICU select message format: https://formatjs.io/guides/message-syntax/#select-format
const toICUSelectMessage = message => `{${message.value.param}, select,
${getObjectEntries(message.value.selectItems).map(_ref2 => {
  let [selectKey, selectMessage] = _ref2;
  return `${selectKey} {${selectMessage}}`;
}).join('\n')}}`;
const toICUMessage = message => {
  if (message && message.value && message.value.pluralItems) return toICUPluralMessage(message);
  if (message && message.value && message.value.selectItems) return toICUSelectMessage(message);
  if (message && message.value && typeof message.value === 'string') return message.value;
  if (typeof message === 'string') return message;
  throw new Error('Unrecognized message schema');
};

// Puff-J File Format: https://w.amazon.com/index.php/INTech/Panther/Architecture/PantherFileFormat/JSON
// React-intl ICU message format: https://github.com/formatjs/react-intl/blob/master/docs/Components.md#message-syntax
const toICUMessages = puffj => getObjectEntries(puffj.resources).reduce((prior, _ref3) => {
  let [messageId, message] = _ref3;
  return {
    ...prior,
    [messageId]: toICUMessage(message)
  };
}, {});

/* eslint-disable @typescript-eslint/no-explicit-any, no-console */

/**
 * Function to retrieve all translations for a locale
 * @param {string} locale
 * @return raw translations without intl formatting
 */
const getTranslations = async locale => {
  let translations = {};

  /**
   * currencySelector-{locale}.puff.json file needs to be present
   * in translation/components dir for this logic to work.
   * Fallback to default locale if no locale is specified.
   */
  if (!locale || !localeToTranslations$1[locale]) {
    translations = await localeToTranslations$1[DEFAULT_LOCALE]();
  } else {
    translations = await localeToTranslations$1[locale]();
  }
  const icu = toICUMessages(translations);
  return icu;
};
const translateCurrencyLabel = (currencies, translate) => {
  return currencies.map(currency => {
    return {
      ...currency,
      label: getCurrencyLabel({
        translate,
        ...currency
      })
    };
  });
};
const translateMessage = (translations, id, locale, params) => {
  const translation = translations[id];
  if (!translation) {
    return id;
  }
  if (params) {
    const formattedMessage = new IntlMessageFormat(translation, locale);
    return `${formattedMessage.format({
      ...params
    })}`;
  }
  return translation;
};
const getCurrencyLabel = _ref => {
  let {
    translate,
    label,
    code,
    symbol
  } = _ref;
  return `${translate(label)} (${code} ${symbol})`;
};
const getCurrencyInfo = async _ref2 => {
  let {
    currencyCode,
    locale
  } = _ref2;
  if (!currencyCode) {
    throw new Error('Currency code is required.');
  }
  const windowCurrencyConfig = window.CurrencyConfig;
  if (!windowCurrencyConfig || windowCurrencyConfig.currencyList === undefined) {
    throw new Error('CurrencyConfig is not available.');
  }
  const currencyInfo = windowCurrencyConfig.currencyList.find(currency => {
    return currency.code === currencyCode;
  });
  if (!currencyInfo) {
    return;
  }
  const translations = await getTranslations(locale);
  return {
    ...currencyInfo,
    label: translateMessage(translations, currencyCode, locale, null)
  };
};

const LoadingIcon = /*#__PURE__*/styled(Icon).withConfig({
  componentId: "sc-1c5s2d-0"
})(["margin-inline-end:", ";vertical-align:middle;"], _ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.xxxsmall.default;
});
const LoadingSpinner = _ref2 => {
  let {
    loadingText
  } = _ref2;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LoadingIcon, {
    type: "spinner"
  }), loadingText && /*#__PURE__*/React.createElement(Text, {
    inline: true,
    size: "xsmall"
  }, loadingText));
};
const LoadingSpinner$1 = LoadingSpinner;

const fallbackTranslateContext = {
  translate: key => key
};
const TranslationContext = /*#__PURE__*/React.createContext(fallbackTranslateContext);
const useTranslation = () => {
  const context = React.useContext(TranslationContext);
  if (!context) {
    throw new Error('TranslationContext must be used with TranslationProvider');
  }
  return context;
};
const TranslationProvider = _ref => {
  let {
    children,
    locale = DEFAULT_LOCALE
  } = _ref;
  const [translations, setTranslations] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    getTranslations(locale).then(result => {
      setTranslations(result);
      setLoaded(true);
    });
  }, [locale]);
  const translate = (id, params) => {
    return translateMessage(translations, id, locale, params);
  };
  return /*#__PURE__*/React.createElement(TranslationContext.Provider, {
    value: {
      translate
    }
  }, loaded ? children : /*#__PURE__*/React.createElement(LoadingSpinner$1, {
    loadingText: translate('loading', undefined)
  }));
};

const transformCurrencyList = (currencies, translate) => {
  const translatedCurrencyList = translateCurrencyLabel(currencies, translate);
  return sortCurrencyList(translatedCurrencyList);
};
const sortCurrencyList = currencies => {
  return currencies.sort((currencyA, currencyB) => {
    return currencyA.code.localeCompare(currencyB.code);
  });
};

const CurrencySelectorFilterRadioGroup = _ref => {
  let {
    id,
    currencyList,
    selected,
    onChange
  } = _ref;
  const {
    translate
  } = useTranslation();
  const transformedCurrencyList = transformCurrencyList(currencyList, translate);
  const [filteredCurrencies, setFilteredCurrencies] = useState(transformedCurrencyList);
  const options = {
    keys: ['code', 'label', 'symbol'],
    threshold: 0.4
  };
  const handleFilterClear = () => {
    setFilteredCurrencies([...transformedCurrencyList]);
  };
  const handleFilterChange = results => {
    setFilteredCurrencies(results);
  };
  const overrideLabel = (label, value) => {
    if (value) {
      const selectedCurrency = filteredCurrencies.find(currency => currency.code === value);
      if (selectedCurrency) {
        const {
          label
        } = selectedCurrency;
        return label;
      }
    }
    return label;
  };
  return /*#__PURE__*/React.createElement(FilterRadioGroup, {
    searchInputId: "currency-search-input",
    placeholder: translate('searchPlaceholder'),
    onChange: onChange,
    onFilterChange: handleFilterChange,
    onFilterClear: handleFilterClear,
    onOverrideLabel: overrideLabel,
    selectedValue: selected.code,
    options: options,
    items: transformedCurrencyList,
    radioGroupName: "currencySelector",
    radioGroupLabel: "currencySelector",
    hideSearchInputLabel: false
  }, filteredCurrencies.map(_ref2 => {
    let {
      code,
      label
    } = _ref2;
    return /*#__PURE__*/React.createElement(RadioButton, {
      id: `${id}-radioButton-${code}`,
      key: code,
      value: code,
      label: label
    });
  }));
};
const CurrencySelectorFilterRadioGroup$1 = CurrencySelectorFilterRadioGroup;

const CurrencySelectorFilterDropdown = _ref => {
  let {
    id,
    currencyList,
    onChange,
    selected
  } = _ref;
  const {
    translate
  } = useTranslation();
  const transformedCurrencyList = transformCurrencyList(currencyList, translate);
  const [filteredCurrencies, setFilteredCurrencies] = useState(transformedCurrencyList);
  const options = {
    keys: ['code', 'label', 'symbol'],
    threshold: 0.4
  };
  const handleFilterClear = () => {
    setFilteredCurrencies([...transformedCurrencyList]);
  };
  const handleFilterChange = results => {
    setFilteredCurrencies(results);
  };
  return /*#__PURE__*/React.createElement(FilterDropdown, {
    searchInputId: "currency-search-input",
    placeholder: translate('searchPlaceholder'),
    onChange: onChange,
    onFilterChange: handleFilterChange,
    onFilterClear: handleFilterClear,
    selectedValue: selected.code,
    options: options,
    items: transformedCurrencyList,
    small: true
  }, filteredCurrencies.map(_ref2 => {
    let {
      code,
      label
    } = _ref2;
    return /*#__PURE__*/React.createElement(DropdownItem, {
      id: `${id}-dropdownItem-${code}`,
      key: code,
      value: code
    }, label);
  }));
};
const CurrencySelectorFilterDropdown$1 = CurrencySelectorFilterDropdown;

const ChangeCurrencyButton = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    translate
  } = useTranslation();
  return /*#__PURE__*/React.createElement(Button, rest, translate('changeCurrency'));
};
const ChangeCurrencyButton$1 = ChangeCurrencyButton;

const StyledDiv = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-13mw47g-0"
})(["display:flex;align-items:center;gap:", ";margin-bottom:", ";", ""], _ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.xxxsmall.default;
}, _ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing.xxsmall.default;
}, isMobileStyleMixin(css(["margin-bottom:", ";gap:", ";"], _ref3 => {
  let {
    theme
  } = _ref3;
  return theme.spacing.xxsmall.mobile;
}, _ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing.xxxsmall.mobile;
})));
const CurrencySelectorLabel = _ref5 => {
  let {
    id
  } = _ref5;
  const {
    translate
  } = useTranslation();
  return /*#__PURE__*/React.createElement(StyledDiv, null, /*#__PURE__*/React.createElement(Text, {
    renderAs: "span",
    size: "xsmall",
    weight: "bold"
  }, `${translate('currency')} (${translate('converted')})`), /*#__PURE__*/React.createElement(HelpTip, {
    id: `${id}-header-helptip`,
    position: "right"
  }, /*#__PURE__*/React.createElement(Text, null, translate('currencyLabelTooltip'))));
};
const CurrencySelectorLabel$1 = CurrencySelectorLabel;

const ContentContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-fp9uvc-0"
})(["display:flex;", ""], isMobileStyleMixin(css(["flex-direction:column;"])));
const Container = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-fp9uvc-1"
})(["display:flex;flex-direction:column;"]);
const StyledChangeCurrencyButton = /*#__PURE__*/styled(ChangeCurrencyButton$1).withConfig({
  componentId: "sc-fp9uvc-2"
})(["margin:", ";", ""], _ref => {
  let {
    theme
  } = _ref;
  return `0 0 0 ${theme.spacing.xsmall.default}`;
}, isMobileStyleMixin(css(["margin:", ";"], _ref2 => {
  let {
    theme
  } = _ref2;
  return `${theme.spacing.xsmall.mobile} 0 0 0`;
})));
const CurrencySelectorContent = _ref3 => {
  let {
    id,
    children,
    onApplyChange,
    isCurrencyChangeEnabled,
    isChanged
  } = _ref3;
  return /*#__PURE__*/React.createElement(Container, null, isCurrencyChangeEnabled && /*#__PURE__*/React.createElement(CurrencySelectorLabel$1, {
    id: id
  }), /*#__PURE__*/React.createElement(ContentContainer, null, children, isCurrencyChangeEnabled && /*#__PURE__*/React.createElement(StyledChangeCurrencyButton, {
    onClick: onApplyChange,
    disabled: !isChanged,
    small: true
  })));
};
const CurrencySelectorContent$1 = CurrencySelectorContent;

/* eslint-disable @typescript-eslint/no-explicit-any */
const fallbackCurrencyConfig = {
  currencyList: [],
  selectedCurrency: {
    code: '',
    label: '',
    symbol: ''
  }
};
const CurrencyConfigContext = /*#__PURE__*/React.createContext(fallbackCurrencyConfig);
const useCurrencyConfigContext = () => {
  const context = React.useContext(CurrencyConfigContext);
  if (!context) {
    throw new Error('CurrencyConfigContext must be used with CurrencyConfigProvider');
  }
  return context;
};
const CurrencyConfigProvider = _ref => {
  let {
    children,
    currencyConfig
  } = _ref;
  const [currencyConfigState, setCurrencyConfigState] = React.useState(currencyConfig || {});
  useEffect(() => {
    try {
      const windowCurrencyConfig = window.CurrencyConfig;
      windowCurrencyConfig && setCurrencyConfigState(windowCurrencyConfig);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('failed to load currencyConfig data');
    }
  }, []);
  const currencyConfigContextValue = useMemo(() => {
    if (!currencyConfigState || currencyConfigState.currencyList === undefined) {
      return null;
    }
    return {
      ...currencyConfigState
    };
  }, [currencyConfigState]);

  // do not render anything until currency info is set
  if (!currencyConfigContextValue) {
    return null;
  }
  return /*#__PURE__*/React.createElement(CurrencyConfigContext.Provider, {
    value: currencyConfigContextValue
  }, children);
};
const withCurrencyInfo = Component => {
  return _ref2 => {
    let {
      ...props
    } = _ref2;
    return /*#__PURE__*/React.createElement(CurrencyConfigProvider, null, /*#__PURE__*/React.createElement(Component, props));
  };
};

const CurrencySelector$1 = _ref => {
  let {
    id = 'currency-selector',
    locale,
    onApplyChange,
    onSelect,
    selectedCurrencyCode,
    isCurrencyChangeEnabled = true
  } = _ref;
  const isMobile = useIsMobile();
  const {
    currencyList,
    selectedCurrency
  } = useCurrencyConfigContext();
  const appliedCurrency = useMemo(() => {
    let currency = selectedCurrency;
    if (selectedCurrencyCode && selectedCurrencyCode !== currency.code) {
      currency = currencyList.find(currency => currency.code === selectedCurrencyCode);
    }
    return currency ? currency : selectedCurrency;
  }, []);
  const [selected, setSelected] = useState(appliedCurrency);
  const handleChange = value => {
    const selectedCurrency = currencyList.find(currency => currency.code === value);
    if (selectedCurrency) {
      onSelect === null || onSelect === void 0 ? void 0 : onSelect(selectedCurrency);
      setSelected(selectedCurrency);
    }
  };
  const applyChange = event => {
    onApplyChange === null || onApplyChange === void 0 ? void 0 : onApplyChange(selected, event);
  };
  const renderFilterRadioGroup = () => {
    return /*#__PURE__*/React.createElement(CurrencySelectorContent$1, {
      id: id,
      onApplyChange: applyChange,
      isCurrencyChangeEnabled: isCurrencyChangeEnabled,
      isChanged: selectedCurrency.code !== selected.code
    }, /*#__PURE__*/React.createElement(CurrencySelectorFilterRadioGroup$1, {
      id: id,
      currencyList: currencyList,
      selected: selected,
      onChange: handleChange
    }));
  };
  const renderFilterDropdown = () => {
    return /*#__PURE__*/React.createElement(CurrencySelectorContent$1, {
      id: id,
      onApplyChange: applyChange,
      isCurrencyChangeEnabled: isCurrencyChangeEnabled,
      isChanged: selectedCurrency.code !== selected.code
    }, /*#__PURE__*/React.createElement(CurrencySelectorFilterDropdown$1, {
      id: id,
      currencyList: currencyList,
      selected: selected,
      onChange: handleChange
    }));
  };
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(LoadingSpinner$1, null)
  }, /*#__PURE__*/React.createElement(TranslationProvider, {
    locale: locale
  }, isMobile ? renderFilterRadioGroup() : renderFilterDropdown()));
};

const CurrencySelector = CurrencySelector$1;

export { CurrencyConfigProvider, CurrencySelector, getCurrencyInfo, useCurrencyConfigContext, withCurrencyInfo };
//# sourceMappingURL=index.js.map
